<template>
	<div
		class="user-chat-activity"
		:class="{ 'has-preview': !!selectedActivity }"
	>
		<div class="activity-pane">
			<header class="activity-header" v-if="!loading">
				<div class="header-content">
					<button
						v-if="showBackButton"
						type="button"
						class="back-button"
						@click="goBack"
						:aria-label="$t('common_button_back')"
					>
						<ChevronIcon left type="regular" class="back-icon" />
						<span class="back-label" v-text="backLabel" />
					</button>

					<div class="name-card-wrap" :class="{ collapsed: isScrolled }">
						<div class="name-card">
							<ProfilePic
								v-if="person"
								:user="profileUser"
								size="48px"
								class="name-avatar"
							/>
							<div class="name-text">
								<div class="name-title" v-text="person?.fullname" />
								<Popover
									v-if="canChangeScope"
									ref="scopePopover"
									contentClass="scope-popover"
									@open="isScopeOpen = true"
									@close="isScopeOpen = false"
								>
									<button
										type="button"
										class="scope-trigger"
										:class="{ open: isScopeOpen }"
										:aria-label="$t('admin_activity_scope_select_label')"
										:aria-expanded="isScopeOpen"
									>
										<span class="scope-trigger-text">
											<i18n-t keypath="admin_activity_scope_trigger" tag="span">
												<template #Label>
													<span class="scope-trigger-label">{{
														scopeSelectedLabel
													}}</span>
												</template>
												<template #ChatName>
													<span class="scope-trigger-chat">{{
														chat?.title
													}}</span>
												</template>
											</i18n-t>
										</span>
										<ChevronIcon
											class="scope-trigger-chevron"
											down
											type="regular"
										/>
									</button>
									<template #content>
										<div class="scope-menu">
											<button
												type="button"
												class="scope-item"
												:class="{ selected: scope.type === 'all' }"
												@click="setScope({ type: 'all' })"
											>
												<div class="scope-item-title">
													{{ $t('admin_activity_scope_all_label') }}
												</div>
												<div class="scope-item-desc">
													{{ $t('admin_activity_scope_all_desc') }}
												</div>
											</button>
											<div class="scope-divider"></div>
											<button
												type="button"
												class="scope-item"
												:class="{ selected: scope.type === 'main' }"
												@click="setScope({ type: 'main' })"
											>
												<div class="scope-item-title">
													{{ $t('admin_activity_scope_main_label') }}
												</div>
												<div class="scope-item-desc">
													{{ $t('admin_activity_scope_main_desc') }}
												</div>
											</button>
											<template v-if="threads.length">
												<div class="scope-divider"></div>
												<div class="scope-section-heading">
													{{ $t('admin_activity_scope_threads_heading') }}
												</div>
												<button
													v-for="thread in threads"
													:key="thread.id"
													type="button"
													class="scope-item compact"
													:class="{
														selected:
															scope.type === 'thread' &&
															scope.threadId === thread.id,
													}"
													@click="
														setScope({
															type: 'thread',
															threadId: thread.id,
														})
													"
												>
													<ReelIcon class="scope-item-icon" type="solid" />
													<div class="scope-item-title">
														{{ thread.title }}
													</div>
												</button>
											</template>
										</div>
									</template>
								</Popover>
								<div v-else class="name-subtitle">
									<i18n-t keypath="admin_activity_subtitle" tag="span">
										<template #ChatName>
											<span class="chat-name">{{ chat?.title }}</span>
										</template>
									</i18n-t>
								</div>
							</div>
						</div>
						<div class="header-divider" />
					</div>

					<div class="filter-section">
						<div class="filter-pills">
							<button
								v-for="filter in filters"
								:key="filter.key"
								type="button"
								class="pill"
								:class="[
									`pill-${filter.key}`,
									{ selected: activeFilter === filter.key },
								]"
								@click="activeFilter = filter.key"
							>
								<component
									:is="filter.icon"
									v-if="filter.icon"
									type="regular"
									class="pill-icon"
								/>
								<span>
									{{ $t(filter.labelKey, { Count: countFor(filter.key) }) }}
								</span>
							</button>
						</div>
						<!--
					Hidden during development — revisit when we decide whether
					"Clear all filters" stays in the final UX. The button, its
					handler, and the `filtersActive` computed are left in place
					so we can re-enable by unhiding this block.
				-->
						<!-- <div class="status-row" v-if="filtersActive">
					<button type="button" class="clear-filters" @click="clearFilters">
						{{ $t('admin_activity_clear_filters') }}
					</button>
				</div> -->
					</div>
				</div>
			</header>

			<main
				class="activity-list-container"
				v-if="!loading && !fetchError"
				ref="scrollContainer"
				@scroll="onScroll"
			>
				<div class="activity-list" v-if="filteredItems.length">
					<ActivityCard
						v-for="(item, idx) in filteredItems"
						:key="itemKey(item, idx)"
						:item="item"
						:chatId="chatId"
						:user="person"
						@select-message="selectedActivity = $event"
					/>
					<div ref="sentinel" class="load-sentinel" />
					<div class="load-more-spinner" v-if="loadingMore">
						<Spinner />
					</div>
				</div>
				<EmptyState
					v-else
					:icon="FolderOpenIcon"
					:buttonText="filtersActive ? $t('admin_activity_empty_view_all') : ''"
					@click="clearFilters"
				>
					<template #text>
						<div class="empty-title">{{ $t('admin_activity_empty') }}</div>
						<div v-if="filtersActive" class="empty-subtitle">
							{{ $t('admin_activity_empty_filtered_subtitle') }}
						</div>
					</template>
					<template v-if="!filtersActive" #button><span /></template>
				</EmptyState>
			</main>

			<CloudLoader class="loading-cover" animate v-if="loading" />
			<FullPageError v-if="fetchError" @retry="retry" />
		</div>
		<aside v-if="selectedActivity" class="preview-pane">
			<ActivityChatPreview
				:key="`${selectedActivity.chatId}-${selectedActivity.messageId}`"
				:chatId="selectedActivity.chatId"
				:messageId="selectedActivity.messageId"
				:chatName="chat?.title || ''"
				@close="selectedActivity = null"
			/>
		</aside>
		<MediaModal />
	</div>
</template>

<script>
import {
	ProfilePic,
	Spinner,
	CloudLoader,
	Popover,
	MediaModal,
} from '@/components/common'
import {
	ChevronIcon,
	MessageIcon,
	ReplyIcon,
	FaceSmileIcon,
	SquarePollHorizontalIcon,
	TrashCanIcon,
	CircleExclamationIcon,
	FolderOpenIcon,
	ReelIcon,
} from '@/icons'
import { EmptyState, FullPageError, get } from '../common'
import store, { normalizeMessage, setUser, getOrganization } from '@/store'
import { ACTIVITY_LOG } from '@/helpers/flags.js'
import { fetchUserInfo } from '@/api/user'
import { fetchThreadsForChat } from '@/api/thread'
import { canRetryAPIError } from '@/helpers/utils'
import { switchGlobalColorScheme } from '@/helpers/colors'
import { colorSchemes } from '@/helpers/constants'
import { markRaw } from 'vue'
import ActivityCard from './ActivityCard'
import ActivityChatPreview from './ActivityChatPreview'

const PAGE_SIZE = 25

// Seed the in-memory user cache (no IndexedDB write) so mention/author lookups
// resolve correctly during render, even on first-time viewers.
const seedUser = (u) => {
	if (u && typeof u === 'object' && u.id) setUser(u, true)
}

const seedInlineUsers = (msg) => {
	seedUser(msg?.user)
	msg?.mentions?.forEach(seedUser)
}

const FILTERS = [
	{ key: 'all', labelKey: 'admin_activity_filter_all', icon: null },
	{
		key: 'message',
		labelKey: 'admin_activity_filter_messages',
		icon: markRaw(MessageIcon),
	},
	{
		key: 'reply',
		labelKey: 'admin_activity_filter_replies',
		icon: markRaw(ReplyIcon),
	},
	{
		key: 'reaction',
		labelKey: 'admin_activity_filter_reactions',
		icon: markRaw(FaceSmileIcon),
	},
	{
		key: 'poll_response',
		labelKey: 'admin_activity_filter_poll_responses',
		icon: markRaw(SquarePollHorizontalIcon),
	},
	{
		key: 'chat_event',
		labelKey: 'admin_activity_filter_events',
		icon: markRaw(CircleExclamationIcon),
	},
	// Hidden during development — revisit when we decide whether the
	// "Deleted" filter belongs in the final UX. The `deleted` branches in
	// filteredItems / countFor and the TrashCanIcon import are left in
	// place so we can re-enable by unhiding this entry.
	// {
	// 	key: 'deleted',
	// 	labelKey: 'admin_activity_filter_deleted',
	// 	icon: markRaw(TrashCanIcon),
	// },
]

export default {
	name: 'UserChatActivity',

	components: {
		ProfilePic,
		Spinner,
		CloudLoader,
		ChevronIcon,
		ReelIcon,
		Popover,
		EmptyState,
		FullPageError,
		ActivityCard,
		ActivityChatPreview,
		MediaModal,
	},

	props: {
		chatId: { type: [Number, String], required: true },
		userId: { type: [Number, String], required: true },
	},

	data() {
		return {
			FolderOpenIcon: markRaw(FolderOpenIcon),
			filters: FILTERS,
			items: [],
			counts: {},
			nextCursor: null,
			chat: null,
			person: null,
			loading: true,
			loadingMore: false,
			fetchError: false,
			observer: null,
			activeFilter: 'all',
			isScrolled: false,
			selectedActivity: null,
			threads: [],
			// { type: 'all' } | { type: 'main' } | { type: 'thread', threadId }
			scope: { type: 'all' },
			isScopeOpen: false,
			// Bumped on every scope change / route reload. Each fetchActivity
			// and fetchCounts captures the token at the start; if it changes
			// before the await resolves, the response is dropped so a stale
			// in-flight loadMore can't push items from the previous scope.
			fetchToken: 0,
		}
	},

	computed: {
		profileUser() {
			// ProfilePic derives "has a picture" from the authoritative
			// `profile_pic_url`, so pass the person object through as-is.
			return this.person
		},

		backRouteName() {
			return this.$route.name === 'user-chat-activity' ? 'user' : 'group'
		},

		// Iframe routes have no admin parent page to return to — hide the
		// chevron entirely. The admin-flow routes always show it.
		isIframeRoute() {
			return String(this.$route.name || '').startsWith('iframe-')
		},

		// True only when this activity route is actually embedded in a
		// (cross-origin) iframe — e.g. Canvas SpeedGrader. Home isn't mounted
		// there to mint the media token, and the app cookie isn't sent, so we
		// mint it ourselves. Opened directly in a tab the cookie works, so we
		// skip minting to keep media CDN-cacheable.
		isEmbeddedIframe() {
			return this.isIframeRoute && window.self !== window.top
		},

		showBackButton() {
			return !this.isIframeRoute
		},

		backLabel() {
			if (this.backRouteName === 'user') {
				return this.person?.fullname || this.$t('common_button_back')
			}
			return this.chat?.title || this.$t('common_button_back')
		},

		filteredItems() {
			if (this.activeFilter === 'all') return this.items
			if (this.activeFilter === 'deleted') {
				return this.items.filter((item) => item.message?.deleted_at)
			}
			return this.items.filter((item) => item.type === this.activeFilter)
		},

		filtersActive() {
			return this.activeFilter !== 'all'
		},

		// Hide the scope dropdown when this page was opened on a thread chat —
		// threads can't have sub-threads, so the scope choice is meaningless.
		canChangeScope() {
			return !this.chat?.is_thread
		},

		scopeSelectedLabel() {
			if (this.scope.type === 'main') {
				return this.$t('admin_activity_scope_main_label')
			}
			if (this.scope.type === 'thread') {
				// Resolve from the live thread list so Pusher renames flow through.
				const thread = this.threads.find((t) => t.id === this.scope.threadId)
				return thread?.title || ''
			}
			return this.$t('admin_activity_scope_all_label')
		},

		activityTargetChatId() {
			return this.scope.type === 'thread' ? this.scope.threadId : this.chatId
		},

		activityScopeParams() {
			// Server validator rejects stringified booleans — pass 1/0.
			if (this.scope.type === 'all') return { include_thread_activity: 1 }
			if (this.scope.type === 'main') return { include_thread_activity: 0 }
			return {}
		},
	},

	created() {
		// Activity feed is designed for light mode only — same approach as
		// OrgManagement until dark variants are designed.
		switchGlobalColorScheme(colorSchemes.LIGHT)
		// Non-reactive handle for the scroll-handler rAF (see onScroll).
		this.scrollRaf = null
	},

	async mounted() {
		// Org-level feature flag gate. The org hydrates during App boot (admin
		// flow) or after auth (iframe/canvas launches), so only block once we
		// can prove the flag is off — an unloaded org falls through to the
		// backend's 403, which is the auth surface for the iframe routes.
		const org = getOrganization()
		if (org && !org[ACTIVITY_LOG]) {
			if (this.isIframeRoute) {
				// No in-app page to fall back to in an embed — surface the same
				// error state the backend 403 would have produced.
				this.loading = false
				this.fetchError = true
			} else {
				this.$router.replace({ name: 'overview' })
			}
			return
		}
		await this.loadAll()
		this.$nextTick(this.setupObserver)
	},

	beforeUnmount() {
		this.observer?.disconnect()
		if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf)
	},

	beforeRouteUpdate(to, from, next) {
		const changed =
			to.params.chatId !== from.params.chatId ||
			to.params.userId !== from.params.userId
		next()
		if (changed) {
			this.$nextTick(async () => {
				await this.loadAll()
				this.$nextTick(this.setupObserver)
			})
		}
	},

	methods: {
		countFor(key) {
			if (key === 'all') return this.counts.total_count ?? 0
			// Server doesn't return a deleted_count — it's a client-side filter.
			// Best-effort count from loaded items; will grow as the user scrolls.
			if (key === 'deleted') {
				return this.items.filter((i) => i.message?.deleted_at).length
			}
			return this.counts[`${key}_count`] ?? 0
		},

		async loadAll() {
			this.loading = true
			// Home mints the media token in the normal app, but the standalone
			// activity iframe has no Home — mint it here before fetching so the
			// media URLs baked during message normalization carry ?t=<token>.
			if (this.isEmbeddedIframe) await store.auth.ensureMediaToken()
			this.fetchError = false
			this.items = []
			this.nextCursor = null
			this.scope = { type: 'all' }
			this.threads = []
			// Reset view state too, so an in-place navigation (beforeRouteUpdate
			// with new chatId/userId) doesn't keep a stale preview pane, filter,
			// or scroll-collapsed header from the previous subject.
			this.selectedActivity = null
			this.activeFilter = 'all'
			this.isScrolled = false
			this.fetchToken++
			await Promise.all([
				this.fetchChat(),
				this.fetchUser(),
				this.fetchActivity(),
				this.fetchCounts(),
				this.fetchThreads(),
			])
			this.loading = false
		},

		async fetchThreads() {
			try {
				const resp = await fetchThreadsForChat(this.chatId, {
					'page[size]': 50,
				})
				this.threads = resp.data || []
			} catch (e) {
				// Non-fatal — the dropdown still works with just All / Main Chat.
				console.error(e)
				this.threads = []
			}
		},

		async fetchChat() {
			try {
				// Iframe viewers can't reach org-admin endpoints. Use the
				// non-admin chat endpoint for them and treat any failure as
				// non-fatal — the chat title is decorative; the feed is core.
				if (this.isIframeRoute) {
					const { data } = await get(`/chats/${this.chatId}`)
					this.chat = data
				} else {
					const resp = await get(`/organization/groups/${this.chatId}`)
					this.chat = resp.data
				}
			} catch (e) {
				console.error(e)
				if (!this.isIframeRoute && !canRetryAPIError(e)) this.fetchError = true
			}
		},

		async fetchUser() {
			try {
				// Iframe viewers can't reach the org-admin user endpoint. Use
				// the general user.info RPC for them and treat any failure as
				// non-fatal — avatar/name are decorative; the feed is core.
				if (this.isIframeRoute) {
					const { user } = await fetchUserInfo(this.userId)
					this.person = user
				} else {
					const resp = await get(`/organization/users/${this.userId}`)
					this.person = resp.data
				}
			} catch (e) {
				console.error(e)
				if (!this.isIframeRoute && !canRetryAPIError(e)) this.fetchError = true
			}
		},

		async fetchActivity({ cursor } = {}) {
			const token = this.fetchToken
			// Every fetch bakes its own media URLs — refresh the token first
			// so a long grading session doesn't fetch with an expired one
			// (covers loadMore pagination and setScope reloads alike).
			// Idempotent: no-ops while >6 min remain.
			if (this.isEmbeddedIframe) await store.auth.ensureMediaToken()
			const params = {
				'page[size]': PAGE_SIZE,
				...this.activityScopeParams,
			}
			if (cursor) params.cursor = cursor
			try {
				const resp = await get(
					`/chats/${this.activityTargetChatId}/users/${this.userId}/activity`,
					params
				)
				if (token !== this.fetchToken) return
				const incoming = (resp.data || []).map((item) => {
					if (item.message) {
						seedInlineUsers(item.message)
						item.message =
							normalizeMessage(item.message, { skipStore: true }) ||
							item.message
					}
					if (item.parent) {
						seedInlineUsers(item.parent)
						item.parent =
							normalizeMessage(item.parent, { skipStore: true }) || item.parent
					}
					return item
				})
				if (cursor) this.items.push(...incoming)
				else this.items = incoming
				this.nextCursor = resp.cursors?.next ?? null
			} catch (e) {
				if (token !== this.fetchToken) return
				console.error(e)
				if (e.response?.status === 403) {
					// Iframe sessions have no admin parent to fall back to —
					// redirecting would land them on /organization/... which
					// the admin role check would bounce. Surface the error
					// inline instead.
					if (this.isIframeRoute) {
						this.fetchError = true
						return
					}
					this.$alert.error(this.$t('admin_activity_no_permission'))
					this.$router.replace({
						name: this.backRouteName,
						params:
							this.backRouteName === 'user'
								? { id: this.userId }
								: { id: this.chatId },
					})
					return
				}
				this.$alert.error(this.$t('common_api_error_save'))
			}
		},

		async fetchCounts() {
			const token = this.fetchToken
			try {
				const resp = await get(
					`/chats/${this.activityTargetChatId}/users/${this.userId}/activity/counts`,
					this.activityScopeParams
				)
				if (token !== this.fetchToken) return
				this.counts = resp.data || {}
			} catch (e) {
				if (token !== this.fetchToken) return
				// counts are nice-to-have; leave at 0 on failure
				console.error(e)
				this.counts = {}
			}
		},

		async setScope(next) {
			if (
				next.type === this.scope.type &&
				next.threadId === this.scope.threadId
			) {
				return
			}
			this.scope = next
			this.items = []
			this.nextCursor = null
			// Reset pill filter — a stale "reply" pill across scopes is jarring.
			this.activeFilter = 'all'
			this.fetchToken++
			this.$refs.scopePopover?.closePopover()
			await Promise.all([this.fetchActivity(), this.fetchCounts()])
			// Clearing items unmounts the .activity-list block, which
			// destroys the sentinel ref the IntersectionObserver was watching.
			// Re-attach to the freshly-mounted sentinel after items repopulate.
			this.$nextTick(this.setupObserver)
		},

		async loadMore() {
			if (!this.nextCursor || this.loadingMore) return
			this.loadingMore = true
			await this.fetchActivity({ cursor: this.nextCursor })
			this.loadingMore = false
		},

		setupObserver() {
			const target = this.$refs.sentinel
			if (!target || typeof IntersectionObserver === 'undefined') return
			this.observer?.disconnect()
			this.observer = new IntersectionObserver(
				(entries) => {
					if (entries.some((e) => e.isIntersecting)) this.loadMore()
				},
				{ rootMargin: '200px' }
			)
			this.observer.observe(target)
		},

		retry() {
			this.loadAll()
		},

		goBack() {
			if (window.history.length > 1) {
				this.$router.go(-1)
				return
			}
			this.$router.push({
				name: this.backRouteName,
				params:
					this.backRouteName === 'user'
						? { id: this.userId }
						: { id: this.chatId },
			})
		},

		clearFilters() {
			this.activeFilter = 'all'
		},

		onScroll() {
			// Collapsing the header grows the (flex-grow) scroll viewport mid-scroll,
			// which makes Firefox clamp scrollTop and re-fire `scroll` synchronously
			// during the transition. A single threshold sitting at the boundary flaps
			// the state back and forth ("funky dance"). Coalesce events into a rAF and
			// use a dead zone — collapse well past the header, only expand near the top.
			if (this.scrollRaf) return
			this.scrollRaf = requestAnimationFrame(() => {
				this.scrollRaf = null
				const top = this.$refs.scrollContainer?.scrollTop ?? 0
				if (!this.isScrolled && top > 56) this.isScrolled = true
				else if (this.isScrolled && top < 8) this.isScrolled = false
			})
		},

		itemKey(item, idx) {
			if (item.type === 'poll_response')
				return `poll-${item.poll?.id}-${item.poll_option?.id}`
			const messageId = item.message?.id ?? idx
			return `${item.type}-${messageId}-${item.occurred_at}`
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.activity-list-container :deep(.empty-state) {
	padding-top: 150px;
	.pronto-icon {
		font-size: 36px;
		color: var(--blue-400);
		margin-bottom: 24px;
	}
	.pronto-btn.primary {
		background: var(--blue-400);
		font-family: Avenir-Medium;
		font-size: 16px;
		line-height: 24px;
		height: 44px;
		border-radius: 10px;
		margin-top: 24px;
		&:not(.disabled):hover {
			background-color: var(--blue-400);
		}
		&:not(.disabled):active {
			background-color: var(--blue-400);
		}
	}
}

.empty-title {
	font-family: Avenir-Heavy;
	font-size: 20px;
	line-height: 30px;
	color: var(--neutral-800);
	text-align: center;
}

.empty-subtitle {
	margin-top: 12px;
	font-family: Avenir-Roman;
	font-size: 16px;
	line-height: 24px;
	color: var(--neutral-600);
	text-align: center;
	max-width: 300px;
}

.user-chat-activity {
	display: flex;
	flex-direction: row;
	height: 100vh;
	width: 100%;
	background: #fafafa;
}

.activity-pane {
	display: flex;
	flex-direction: column;
	flex: 1;
	min-width: 0;
	height: 100%;
	position: relative;
}

.preview-pane {
	flex: 0 0 50%;
	max-width: 50%;
	height: 100%;
	overflow: hidden;
}

.activity-header {
	flex-shrink: 0;
	position: relative;
	// Match the drop shadow on the preview pane's header bar so the two
	// headers read as a unified row across the split view.
	z-index: 1;
	padding: 16px 32px;
	background: var(--neutral-0);
	box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.1), 0 1px 3px 0 rgba(0, 0, 0, 0.1);
}

// Center the header content as a column whose width is driven by the filter
// pills: `fit-content` takes their single-line (max-content) width, so on a wide
// enough screen every pill sits on one row and that row defines the max width.
// The name card stretches to — but never exceeds — that same width. On narrower
// screens the width clamps to the available space and the pills wrap. This lets
// the header grow wider than the centered activity list below it.
.header-content {
	width: fit-content;
	max-width: 100%;
	margin: 0 auto;
	display: flex;
	flex-direction: column;
	gap: 16px;
}

// Divider between the name card and the filter pills. Lives inside
// .name-card-wrap so it collapses together with the card on scroll.
.header-divider {
	margin-top: 16px;
	height: 1px;
	background: var(--neutral-200);
}

.back-button {
	background: none;
	border: none;
	padding: 0;
	display: inline-flex;
	align-items: center;
	gap: 6px;
	font-family: Avenir-Roman;
	font-size: 13px;
	color: var(--neutral-600);
	cursor: pointer;
	align-self: flex-start;

	.back-icon {
		font-size: 12px;
	}

	&:hover {
		color: var(--neutral-800);
	}
}

.name-card-wrap {
	overflow: hidden;
	max-height: 200px;
	transition: max-height 0.2s ease, margin 0.2s ease, opacity 0.15s ease;

	&.collapsed {
		max-height: 0;
		opacity: 0;
		// Cancel the parent flex gap that would otherwise leave 16px of empty
		// space where the card used to be.
		margin-bottom: -16px;
	}
}

.name-card {
	display: flex;
	align-items: center;
	gap: 16px;
	padding: 24px;
	background: #f9fafb;
	border-radius: 10px;
}

.name-avatar {
	flex-shrink: 0;
}

.name-text {
	display: flex;
	flex-direction: column;
	gap: 4px;
	min-width: 0;
}

// The shared Popover wrapper is width: max-content, which pins the 670px scope
// trigger and prevents it from shrinking. Cap it to the available width so the
// trigger stays responsive and the chevron isn't clipped as the pane narrows.
.name-text :deep(.popover-container) {
	max-width: 100%;
}

.name-title {
	font-family: Avenir-Roman;
	font-size: 18px;
	color: var(--neutral-800);
	@include clamp(1);
}

.name-subtitle {
	font-family: Avenir-Roman;
	font-size: 14px;
	color: var(--neutral-600);
	@include clamp(1);

	.chat-name {
		font-family: Avenir-Medium;
		color: var(--neutral-800);
	}
}

.scope-trigger {
	display: inline-flex;
	align-items: center;
	gap: 4px;
	width: 670px;
	max-width: 100%;
	margin: 0;
	padding: 6px 12px;
	background: var(--neutral-0);
	border: 0.9px solid var(--neutral-200);
	border-radius: 6px;
	font-family: Avenir-Roman;
	font-size: 14px;
	line-height: 20px;
	color: var(--neutral-500);
	text-align: left;
	cursor: pointer;
	min-width: 0;

	&:hover {
		border-color: var(--neutral-300);
	}

	// When open, square off the bottom so the trigger reads as attached to
	// the menu panel below it.
	&.open {
		border-bottom-left-radius: 0;
		border-bottom-right-radius: 0;
	}

	.scope-trigger-text {
		flex: 1;
		min-width: 0;
		@include clamp(1);
	}

	.scope-trigger-chat {
		font-family: Avenir-Medium;
		color: var(--neutral-800);
	}

	.scope-trigger-chevron {
		flex-shrink: 0;
		font-size: 14px;
		color: var(--neutral-500);
		transition: transform 0.15s ease;
	}

	&.open .scope-trigger-chevron {
		transform: rotate(180deg);
	}
}

.filter-section {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.filter-pills {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
}

.pill {
	--pill-active-bg: var(--interaction-message-accent);

	display: inline-flex;
	align-items: center;
	gap: 8px;
	padding: 8px 16px;
	border-radius: 10px;
	border: none;
	background: var(--neutral-100);
	color: var(--neutral-800);
	font-family: Avenir-Medium;
	font-size: 14px;
	line-height: 24px;
	cursor: pointer;
	white-space: nowrap;

	&.pill-message,
	&.pill-reply {
		.pill-icon {
			font-size: 18px;
			width: 18px;
			height: 18px;
		}
	}
	&.pill-reply {
		--pill-active-bg: var(--interaction-reply-accent);
	}
	&.pill-reaction {
		--pill-active-bg: var(--interaction-reaction-accent);
	}
	&.pill-poll_response {
		--pill-active-bg: var(--teal-300);
	}
	&.pill-chat_event {
		--pill-active-bg: var(--interaction-event-accent);
	}
	&.pill-deleted {
		--pill-active-bg: var(--interaction-deleted-accent);
	}

	.pill-icon {
		font-size: 16px;
		width: 16px;
		height: 16px;
		flex-shrink: 0;
	}

	&:hover {
		background: var(--neutral-200);
	}

	&.selected {
		background: var(--pill-active-bg);
		color: var(--neutral-0);

		&:hover {
			background: var(--pill-active-bg);
			opacity: 0.95;
		}
	}
}

.status-row {
	display: flex;
	align-items: center;
	gap: 8px;
	font-family: Avenir-Roman;
	font-size: 14px;
	color: var(--neutral-600);
}

.clear-filters {
	background: none;
	border: none;
	padding: 0;
	font-family: Avenir-Medium;
	font-size: 14px;
	color: var(--blue-400);
	text-decoration: underline;
	cursor: pointer;

	&:hover {
		opacity: 0.8;
	}
}

.activity-list-container {
	flex-grow: 1;
	overflow-y: auto;
	padding: 24px 32px;
	// The header above collapses on scroll, which grows this viewport. Firefox's
	// scroll anchoring tries to compensate by nudging scrollTop, feeding the
	// collapse/expand loop. Opt out so the offset stays put.
	overflow-anchor: none;
}

.activity-list {
	display: flex;
	flex-direction: column;
	gap: 12px;
	max-width: 760px;
	margin: 0 auto;
}

.load-sentinel {
	height: 1px;
}

.load-more-spinner {
	@include flex-center;
	padding: 16px 0;
}

.loading-cover {
	@include absolute-cover;
}
</style>

<style lang="scss">
// Unscoped — the popover content is teleported to <body>, so scoped styles
// on this component don't reach it. Namespaced under .scope-popover.
.pronto-popover.scope-popover {
	.scope-menu {
		display: flex;
		flex-direction: column;
		gap: 12px;
		width: 670px;
		max-width: calc(100vw - 32px);
		max-height: 360px;
		overflow-y: auto;
		padding: 10px 12px;
		background: var(--neutral-0);
		border: 1px solid var(--neutral-200);
		// Only round the bottom — the trigger above squares off its bottom
		// when open so the two pieces appear attached.
		border-radius: 0 0 8px 8px;
		box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
			0 4px 6px -4px rgba(0, 0, 0, 0.1);
	}

	.scope-item {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: 0;
		width: 100%;
		margin: 0;
		padding: 8px 8px;
		background: none;
		border: none;
		border-radius: 6px;
		text-align: left;
		cursor: pointer;

		&.compact {
			flex-direction: row;
			align-items: center;
			gap: 8px;

			.scope-item-title {
				padding-bottom: 0;
				padding-left: 6px;
			}

			// Tighten the space between consecutive thread items only — pull
			// each one up to trim the 12px menu gap without touching the
			// item padding (which is what the hover highlight fills, so its
			// height stays the same). Other rows keep the full gap.
			& + & {
				margin-top: -4px;
			}
		}

		&:hover {
			background: var(--neutral-100);
		}

		&.selected {
			background: var(--interaction-message-bg);

			.scope-item-title {
				color: #1447e6;
			}
		}
	}

	.scope-item-title {
		font-family: Avenir-Roman;
		font-size: 16px;
		line-height: 20px;
		color: var(--neutral-800);
		padding-bottom: 4px;
	}

	.scope-item-desc {
		font-family: Avenir-Book;
		font-size: 14px;
		line-height: 16px;
		color: var(--neutral-500);
	}

	.scope-item-icon {
		flex-shrink: 0;
		font-size: 14px;
		color: var(--neutral-500);
		// Match the rotation used everywhere else the reel icon represents
		// threads (e.g. SidebarChat, ChatHeader, ThreadView).
		transform: rotate(30deg);
	}

	.scope-divider {
		flex-shrink: 0;
		width: 100%;
		height: 1px;
		background: var(--neutral-200);
	}

	.scope-section-heading {
		font-family: Avenir-Medium;
		font-size: 13px;
		line-height: 16px;
		color: var(--neutral-500);
		padding-top: 4px;
		padding-left: 8px;
	}
}
</style>
